import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import pdfjs from 'pdfjs-dist';
import entityComposition from '../classes/entityComposition';
import annTools from './annotations/commonAnnotationTools';
import AnnotationsLayer from './pdfAnnotationsLayer';
import { checkPermission, makeResource } from '../../../../behaviors/src/lib/permissions';

/**
 * @function PDFPage
 * takes in a pdf page from PDFJS wrapper and pops it into a canvas element, overlaying a viewport
 * and text extracted from the page.
 */
const PDFPage = _ref => {
  let groupedAnnotations = _ref.annotation.groupedAnnotations,
    _ref$annotationSelect = _ref.annotationSelection,
    annotationContextFromSelection = _ref$annotationSelect.annotationContext,
    _ref$annotationSelect2 = _ref$annotationSelect.selectableType,
    selectableType = _ref$annotationSelect2 === void 0 ? null : _ref$annotationSelect2,
    page = _ref.page,
    _ref$rotate = _ref.rotate,
    rotate = _ref$rotate === void 0 ? 0 : _ref$rotate,
    _ref$zoomFactor = _ref.zoomFactor,
    zoomFactor = _ref$zoomFactor === void 0 ? 1 : _ref$zoomFactor,
    maxPageWidth = _ref.maxPageWidth,
    _ref$selected = _ref.selected,
    selected = _ref$selected === void 0 ? null : _ref$selected,
    pdf = _ref.pdf,
    pageInView = _ref.pageInView,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === void 0 ? null : _ref$onClick,
    _ref$onPageRender = _ref.onPageRender,
    onPageRender = _ref$onPageRender === void 0 ? null : _ref$onPageRender,
    highlightsByPage = _ref.reportAnnotation.highlightsByPage,
    _ref$editMode = _ref.editMode,
    editMode = _ref$editMode === void 0 ? null : _ref$editMode,
    _ref$login = _ref.login,
    login = _ref$login === void 0 ? {} : _ref$login,
    _ref$annotationContex = _ref.annotationContext,
    annotationContext = _ref$annotationContex === void 0 ? null : _ref$annotationContex,
    _ref$evaluateAnnotati = _ref.evaluateAnnotationHandlerColor,
    evaluateAnnotationHandlerColor = _ref$evaluateAnnotati === void 0 ? null : _ref$evaluateAnnotati;
  // Use annotationContext from props if provided, otherwise fall back to annotationSelection
  const resolvedAnnotationContext = annotationContext || annotationContextFromSelection;

  // Check if user can create highlight annotations
  const canCreateHighlightAnnotations = useCallback(() => {
    if (resolvedAnnotationContext !== 'form') {
      return true; // Allow in non-form contexts (report/legacy)
    }
    const user = login === null || login === void 0 ? void 0 : login.user;
    if (!user || !user.permissions) {
      return false;
    }

    // Check if a highlight annotation tool is currently selected
    // The selectableType is a composite identifier: 'renderType__formTypeIdentifier'
    if (!selectableType || !selectableType.startsWith('highlight')) {
      return false; // No highlight tool selected
    }

    // Extract form type identifier from the composite type
    const _selectableType$split = selectableType.split('__'),
      _selectableType$split2 = _slicedToArray(_selectableType$split, 2),
      formTypeIdentifier = _selectableType$split2[1];
    if (!formTypeIdentifier) {
      return false; // No form type identifier found
    }

    // Check if user has permission to CREATE annotations for this specific form type
    // The checkPermission function will automatically fall back to general permissions
    // if no specific form type permission is found
    const resource = makeResource('form', formTypeIdentifier);
    const hasPermission = checkPermission(resource, 'create', user.permissions, user) === true;
    return hasPermission;
  }, [resolvedAnnotationContext, login, selectableType]);

  // Check if user can create ink/line annotations
  const canCreateDrawingAnnotations = useCallback(() => {
    if (resolvedAnnotationContext !== 'form') {
      return true; // Allow in non-form contexts (report/legacy)
    }
    const user = login === null || login === void 0 ? void 0 : login.user;
    if (!user || !user.permissions) {
      return false;
    }

    // Check if an ink or line annotation tool is currently selected
    // The selectableType is a composite identifier: 'renderType__formTypeIdentifier'
    if (!selectableType || !selectableType.startsWith('ink') && !selectableType.startsWith('line')) {
      return false; // No ink/line tool selected
    }

    // Extract form type identifier from the composite type
    const _selectableType$split3 = selectableType.split('__'),
      _selectableType$split4 = _slicedToArray(_selectableType$split3, 2),
      formTypeIdentifier = _selectableType$split4[1];
    if (!formTypeIdentifier) {
      return false; // No form type identifier found
    }

    // Check if user has permission to CREATE annotations for this specific form type
    const resource = makeResource('form', formTypeIdentifier);
    const hasPermission = checkPermission(resource, 'create', user.permissions, user) === true;
    return hasPermission;
  }, [resolvedAnnotationContext, login, selectableType]);

  // Check if user can select existing highlight annotations (requires view_detail permission)
  const canSelectHighlightAnnotations = useCallback(() => {
    if (resolvedAnnotationContext !== 'form') {
      return true; // Allow in non-form contexts (report/legacy)
    }
    const user = login === null || login === void 0 ? void 0 : login.user;
    if (!user || !user.permissions) {
      return false;
    }

    // Check if a highlight annotation tool is currently selected
    // The selectableType is a composite identifier: 'renderType__formTypeIdentifier'
    if (!selectableType || !selectableType.startsWith('highlight')) {
      return false; // No highlight tool selected
    }

    // Extract form type identifier from the composite type
    const _selectableType$split5 = selectableType.split('__'),
      _selectableType$split6 = _slicedToArray(_selectableType$split5, 2),
      formTypeIdentifier = _selectableType$split6[1];
    if (!formTypeIdentifier) {
      return false; // No form type identifier found
    }

    // Check if user has permission to VIEW_DETAIL annotations for this specific form type
    // This allows users to select existing highlights even if they can't create new ones
    const resource = makeResource('form', formTypeIdentifier);
    const hasPermission = checkPermission(resource, 'view_detail', user.permissions, user);
    return hasPermission === true;
  }, [resolvedAnnotationContext, login, selectableType]);

  // Check if user can edit form annotations (move/scale) - requires edit permission
  const canEditFormAnnotations = useCallback(() => {
    if (resolvedAnnotationContext !== 'form') {
      return true; // Allow in non-form contexts (report/legacy)
    }
    const user = login === null || login === void 0 ? void 0 : login.user;
    if (!user || !user.permissions) {
      return false;
    }

    // Check if any annotation tool is currently selected
    // The selectableType is a composite identifier: 'renderType__formTypeIdentifier'
    if (!selectableType) {
      return false; // No tool selected
    }

    // Extract form type identifier from the composite type
    const _selectableType$split7 = selectableType.split('__'),
      _selectableType$split8 = _slicedToArray(_selectableType$split7, 2),
      formTypeIdentifier = _selectableType$split8[1];
    if (!formTypeIdentifier) {
      return false; // No form type identifier found
    }

    // Check if user has permission to EDIT annotations for this specific form type
    const resource = makeResource('form', formTypeIdentifier);
    const hasPermission = checkPermission(resource, 'edit', user.permissions, user);
    return hasPermission === true;
  }, [resolvedAnnotationContext, login, selectableType]);
  const _useState = useState({
      currentlySelected: null,
      zoomFactor,
      inView: false,
      initialRenderComplete: false,
      forceRerender: false,
      zoom: false,
      selected: false,
      previousSelection: null,
      renderInitiated: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const annotationsLayerRef = useRef(null);
  const pdfPageRef = useRef(null);
  const isRenderingRef = useRef(false);
  const scaleRef = useRef(null);
  const pageFetchInitiatedRef = useRef(false);
  const isMountedRef = useRef(true);
  const renderTimeoutRef = useRef(null);
  const renderPage = useCallback(async pdfPage => {
    pdfPageRef.current = pdfPage;
    const canvas = canvasRef.current;
    if (!canvas || !isMountedRef.current) {
      // Canvas not mounted yet or component unmounted, skip rendering
      isRenderingRef.current = false;
      return;
    }
    if (pdfPage && !isRenderingRef.current) {
      // set rendering to true so new re-render calls can't interrupt render
      isRenderingRef.current = true;

      // Mark that render has been initiated so we don't request another one
      if (isMountedRef.current) {
        setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
          renderInitiated: true
        }));
      }

      // pull out canvas 2D context to grab it's properties, make changes and draw on it
      const canvasContext = canvas.getContext('2d', {
        willReadFrequently: true
      });

      // view = [cropBox originX, cropBox originY, cropBox width, cropBox height]
      const view = pdfPage.view;
      const pageWidth = view[2] - view[0];
      const relativePageScale = pageWidth / maxPageWidth;
      const originOffset = {
        x: view[0],
        y: view[1]
      };

      // increasing the pixel density to match device pixelRatio is nice, but causes unnesseary
      // slow down when rendering, was formerly: "window.devicePixelRatio || 1"
      const dpiScale = 1;

      // Calculate a scale factor for the page width vs the canvas width
      const canvasScale = canvas.parentElement.clientWidth / pageWidth;
      // Calculate an adjustment scale factor which takes into canvasScale, dpiScale and zoomFactor
      // relativePageScale takes into account how large this page is compared to the largest page
      const adjustedScale = canvasScale * dpiScale * zoomFactor * relativePageScale;

      // pull out a viewport that takes into account rotation and the adjustment scale factor
      const viewport = pdfPage.getViewport({
        scale: adjustedScale,
        rotation: rotate
      });

      // set the canvas properties and style properties
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      // Store the scale and origin offset in a HTML5 data variables.
      canvas['data-origin'] = originOffset;
      canvas['data-scale'] = adjustedScale;
      canvas['data-pdf-page-height'] = view[3]; // used to figure out where to plot new annotations
      canvas['data-pdf-page-width'] = view[2];
      canvas.style.width = "".concat(viewport.width / dpiScale, "px");
      canvas.style.height = "".concat(viewport.height / dpiScale, "px");

      /* Setup annotation layer overlay */
      const canvasWidth = viewport.width / dpiScale;
      const leftOffset = (canvas.parentElement.clientWidth - canvasWidth) / 2;
      annotationsLayerRef.current.height = viewport.height;
      annotationsLayerRef.current.width = viewport.width;
      textLayerRef.current.height = viewport.height;
      textLayerRef.current.width = viewport.width;
      if (canvasWidth > canvas.parentElement.clientWidth) {
        // If the canvas is larger then the client, we don't need to compute an offset
        annotationsLayerRef.current.style.left = '0px';
        textLayerRef.current.style.left = '0px';
      } else {
        // If the canvas is smaller then the client, set the left offset.
        annotationsLayerRef.current.style.left = "".concat(leftOffset, "px");
        textLayerRef.current.style.left = "".concat(leftOffset, "px");
      }
      annotationsLayerRef.current.style.width = "".concat(viewport.width / dpiScale, "px");
      annotationsLayerRef.current.style.height = "".concat(viewport.height / dpiScale, "px");
      textLayerRef.current.style.width = "".concat(viewport.width / dpiScale, "px");
      textLayerRef.current.style.height = "".concat(viewport.height / dpiScale, "px");

      // render the pdf with the defined canvas and viewport
      try {
        await pdfPage.render({
          canvasContext,
          viewport
        }).promise;
        const textContent = await pdfPage.getTextContent();
        scaleRef.current = adjustedScale;

        // run render complete callback so parent
        // component knows page has completed it's render op
        if (onPageRender) {
          onPageRender(page, viewport.height / dpiScale);
        }
        canvas.scale = adjustedScale;

        // After rendering is complete, reset forceRerender and renderInitiated flags
        if (isMountedRef.current) {
          setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
            forceRerender: false,
            renderInitiated: false,
            initialRenderComplete: true
          }));
        }

        // Reset rendering flag
        isRenderingRef.current = false;
        if (textLayerRef.current) {
          textLayerRef.current.innerHTML = '';
          // Render the text
          pdfjs.renderTextLayer({
            textContent,
            container: textLayerRef.current,
            viewport,
            textDivs: []
          });
        }
      } catch (e) {
        // Handle different types of rendering errors
        if (e.name === 'RenderingCancelledException') {
          // Rendering was cancelled - this is expected when rapid re-renders occur
          // or component unmounts during rendering. Just log at debug level.
          /* eslint-disable-next-line no-console */
          console.debug('PDF rendering cancelled for page', page, e.message);
        } else {
          // Log other rendering errors at error level
          /* eslint-disable-next-line no-console */
          console.error('Error while rendering PDF page', page, ':', e);
        }

        // Always reset rendering flags and state on any error
        isRenderingRef.current = false;
        if (isMountedRef.current) {
          setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
            forceRerender: false,
            renderInitiated: false
          }));
        }
      }
    }
  }, [maxPageWidth, onPageRender, page, rotate, zoomFactor]);

  // Fetch the PDF page only once
  useEffect(() => {
    const shouldFetchPage = (!pdfPageRef.current || pdfPageRef.current.pageNumber !== page) && !pageFetchInitiatedRef.current;
    if (shouldFetchPage) {
      pageFetchInitiatedRef.current = true;
      pdf.getPage(page).then(pdfPage => {
        if (isMountedRef.current) {
          renderPage(pdfPage);
        }
        pageFetchInitiatedRef.current = false;
      });
    }
  }, [pdf, page, renderPage]);

  // Cleanup effect to handle component unmounting
  useEffect(() => () => {
    isMountedRef.current = false;
    // Reset rendering flag on unmount to prevent memory leaks
    isRenderingRef.current = false;
    // Clear any pending render timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
      renderTimeoutRef.current = null;
    }
  }, [page]);

  // This effect handles view state changes but doesn't trigger renders directly
  useEffect(() => {
    /**
     * The goal here is to either permit zooming / selection
     * if the page is in view or to block it until the page
     * is in view (at which point we can trigger a re-render with any
     * of the new props).  We set state variables that dictate what kind
     * of render we should trigger.  Zoom triggers a full re-render while
     * annotation selection triggers a much smaller, localaized re-render.
     */
    const overscan = 4;
    /**
     * in order to be able to jump to an annotation, we need to render the target page when zoom
     * changes, that way we can jump to the x/y location of the annotation accurately
     * (scale changes with zoom)
     */
    const isSelectedItemPage = selected && selected.pageNumber && page === selected.pageNumber;
    // if a page has a selected annotation - we consider it `inView` for render purposes
    const isInView = pageInView + overscan > page && pageInView - overscan < page || isSelectedItemPage;

    // Use functional state updates to avoid stale state closures
    if (isMountedRef.current) {
      setState(prevState => {
        // Skip updates if we're currently rendering
        if (isRenderingRef.current || prevState.renderInitiated) {
          return prevState;
        }

        // Only update if something changed to avoid unnecessary rerenders
        if (!isInView && prevState.inView) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            forceRerender: false,
            inView: false
          });
        }
        if (isInView && !prevState.inView) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            inView: true,
            forceRerender: true
          });
        }
        if (isInView && prevState.zoomFactor !== zoomFactor) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            zoom: true,
            selected: false,
            inView: true,
            forceRerender: true,
            zoomFactor
          });
        }
        if (isInView && (prevState.currentlySelected !== selected || JSON.stringify(prevState.currentlySelected) !== JSON.stringify(selected))) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            zoom: false,
            selected: true,
            inView: true,
            forceRerender: false,
            currentlySelected: selected,
            previousSelection: prevState.currentlySelected
          });
        }

        // Return the same state if nothing changed
        return prevState;
      });
    }
  }, [pageInView, selected, zoomFactor, page, state.renderInitiated]);

  // Completely separate the render trigger from the state updates
  useEffect(() => {
    // Only trigger a rerender if:
    // 1. We need to rerender (zoom or forceRerender)
    // 2. We have a page reference
    // 3. We're not already rendering
    // 4. We haven't already initiated a render for this state change
    if ((state.zoom || state.forceRerender) && pdfPageRef.current && !isRenderingRef.current && !state.renderInitiated) {
      // Immediately mark rendering as initiated to prevent duplicate render requests
      if (isMountedRef.current) {
        setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
          renderInitiated: true,
          // Reset zoom flag immediately to prevent further renders from zoom changes
          zoom: false
        }));
      }

      // Clear any existing render timeout to debounce rapid re-render requests
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }

      // Schedule the actual render with a small delay to debounce rapid changes
      renderTimeoutRef.current = setTimeout(() => {
        if (pdfPageRef.current && !isRenderingRef.current && isMountedRef.current) {
          renderPage(pdfPageRef.current);
        }
        renderTimeoutRef.current = null;
      }, 16); // ~60fps debounce
    }
  }, [state.zoom, state.forceRerender, state.renderInitiated, renderPage, page]);
  const enableTextLayerInteraction = useCallback(() => {
    if (textLayerRef.current) {
      // Enable text layer interaction only if user can create highlight annotations
      // This prevents text selection when user can only view but not create highlights
      const canCreate = canCreateHighlightAnnotations();
      if (canCreate) {
        textLayerRef.current.style.pointerEvents = 'auto';
      } else {
        textLayerRef.current.style.pointerEvents = 'none';
      }
    }
  }, [canCreateHighlightAnnotations]);
  const disableTextLayerInteraction = useCallback(() => {
    if (textLayerRef.current) {
      textLayerRef.current.style.pointerEvents = 'none';
    }
  }, []);
  const handleTextLayerClick = useCallback(event => {
    const textSelection = global.window.getSelection();
    if (textSelection && textSelection.toString()) {
      // Creating new highlight annotation - requires create permission
      if (!canCreateHighlightAnnotations()) {
        return;
      }
      const rectsString = annTools.getRectsStringFromSelection(event, textLayerRef.current, textSelection, page);
      const rectString = annTools.getRectStringFromRectsString(rectsString);
      const clickDetails = {
        pageNumber: page,
        rotation: rotate,
        rectsString,
        rectString
      };
      onClick('stage', null, clickDetails);
      textSelection.removeAllRanges(); // deselect text
    } else {
      var _groupedAnnotations$p;
      // Selecting existing highlight annotation - requires view_detail permission
      if (!canSelectHighlightAnnotations()) {
        return;
      }
      const highlightsForPage = resolvedAnnotationContext === 'form' ? (groupedAnnotations === null || groupedAnnotations === void 0 || (_groupedAnnotations$p = groupedAnnotations[page]) === null || _groupedAnnotations$p === void 0 ? void 0 : _groupedAnnotations$p.Highlight) || [] : highlightsByPage[page] || [];
      const overlappingAnnotations = annTools.getHighlightsFromCursorPosition(event, highlightsForPage, page);
      if (overlappingAnnotations.length > 0) {
        // select the last annotation in the list, it's the most recently created
        onClick('highlight', overlappingAnnotations[overlappingAnnotations.length - 1]);
      } else {
        onClick('stage'); // empty space was selected
      }
    }
  }, [resolvedAnnotationContext, groupedAnnotations, highlightsByPage, onClick, page, rotate, canSelectHighlightAnnotations, canCreateHighlightAnnotations]);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "textLayer textLayer-".concat(page, " t-text-layer-").concat(page, " ").concat(!canCreateHighlightAnnotations() ? 'no-highlight-permission' : ''),
    ref: textLayerRef,
    onMouseUp: handleTextLayerClick,
    role: "presentation"
  }), /*#__PURE__*/React.createElement("canvas", {
    ref: canvasRef,
    className: "pdfpage-".concat(page, " t-pdfpage-").concat(page)
  }), /*#__PURE__*/React.createElement("div", {
    className: "annotationsLayer t-annotations-layer-".concat(page),
    ref: annotationsLayerRef
  }, scaleRef.current ? /*#__PURE__*/React.createElement(AnnotationsLayer, {
    onClick: onClick,
    pageNumber: page,
    forceAnnotationRerender: state.zoom || state.forceRerender ? Math.random() : 0,
    selected: selected && selected.id ? selected.id : '',
    hideTextLayer: disableTextLayerInteraction,
    showTextLayer: enableTextLayerInteraction,
    selectableType: selectableType,
    editMode: editMode,
    canCreateDrawingAnnotations: canCreateDrawingAnnotations,
    canEditFormAnnotations: canEditFormAnnotations,
    evaluateAnnotationHandlerColor: evaluateAnnotationHandlerColor
  }) : null));
};
PDFPage.propTypes = {
  annotation: PropTypes.shape({
    groupedAnnotations: PropTypes.shape({})
  }).isRequired,
  annotationSelection: PropTypes.shape({
    annotationContext: PropTypes.string,
    selectableType: PropTypes.string
  }).isRequired,
  page: PropTypes.number.isRequired,
  rotate: PropTypes.number,
  zoomFactor: PropTypes.number,
  onPageRender: PropTypes.func,
  maxPageWidth: PropTypes.number.isRequired,
  selected: PropTypes.shape({
    id: PropTypes.string,
    pageNumber: PropTypes.number
  }),
  onClick: PropTypes.func,
  pdf: PropTypes.shape({
    getPage: PropTypes.func
  }).isRequired,
  reportAnnotation: PropTypes.shape({
    highlightsByPage: PropTypes.shape({})
  }).isRequired,
  pageInView: PropTypes.number.isRequired,
  selectableType: PropTypes.string,
  editMode: PropTypes.bool,
  login: PropTypes.shape({
    user: PropTypes.shape({
      permissions: PropTypes.shape({})
    })
  }),
  annotationContext: PropTypes.string,
  evaluateAnnotationHandlerColor: PropTypes.func
};
export { PDFPage as TestPDFPage };
export default entityComposition(PDFPage, ['annotation', 'annotationSelection', 'reportAnnotation', 'login']);